import { countryCodes } from '../shared/countryData';

/**
 * ISO codes of the curated (enabled) countries for the origin country filter.
 * Single source of truth is src/shared/countryData.js — uncommenting a country
 * there adds it to the filter.
 */
export const ORIGIN_COUNTRY_CODES = countryCodes.map(c => c.code);

/**
 * Sentinel query value for an applied empty selection. No listing has this as
 * its originCountry (values are always ISO codes), so the search matches
 * nothing. This keeps "nothing selected" distinguishable from "no filter" in
 * the URL, letting the form reopen with everything unchecked.
 */
export const ORIGIN_COUNTRY_NONE = 'none';

/**
 * Format the selected origin countries into a search query value.
 *
 * A selection covering every curated country means "no filter" and maps to
 * null (the query param is dropped), as does a missing selection (the Clear
 * button submits null). An applied empty selection is different: it maps to
 * the "none" sentinel, which matches no listings.
 *
 * @param {Array<string>} selected - selected ISO country codes
 * @returns {string|null} comma-joined codes, the "none" sentinel, or null for "no filter"
 */
export const formatOriginCountryQueryValue = selected => {
  if (!Array.isArray(selected)) {
    return null;
  }
  if (selected.length === 0) {
    return ORIGIN_COUNTRY_NONE;
  }
  const selectedSet = new Set(selected);
  const allSelected = ORIGIN_COUNTRY_CODES.every(code => selectedSet.has(code));
  return allSelected ? null : selected.join(',');
};

/**
 * Parse a search query value back into selected origin country codes.
 * Inverse of formatOriginCountryQueryValue.
 *
 * @param {string|null|undefined} paramValue - query param value
 * @returns {Array<string>|null} selected codes ([] for the "none" sentinel), or null when the filter is not set
 */
export const parseOriginCountryQueryValue = paramValue => {
  if (!paramValue) {
    return null;
  }
  return paramValue === ORIGIN_COUNTRY_NONE ? [] : paramValue.split(',');
};
